import type { TFunction } from 'i18next';

import {
    type FeedingPen,
    type FeedingSourceState,
    type PenMetric,
    type PenWarning,
    type PenWarningTrigger,
    penWarnings,
    penWarningTier,
} from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import { feedVsPlanTone, type KpiTone, sfrTone } from '../feedingChartColors';
import { displayedPercent } from '../percentFormat';
import type { SystemLabels } from '../systemLabel';

import { cellReason, planDaysTitle } from './penCellReason';
import { sourceTitle } from './penComparisonFormat';
import { infoCellClass, warningCellClass } from './penComparisonTones';
import { type InfoBand, infoCellTitle, joinTitles } from './penInfoTitle';

/** How one pen's cells are marked and titled, each by the pen's own config. */
export interface PenRowCells {
    /** The conditions the pen meets. */
    warnings: PenWarning[];
    /** Whether the Bioscope is still collecting, which masks every Bioscope cell. */
    collecting: boolean;
    /** The warning class of a bFCR or RGI cell. */
    breach: (trigger: PenWarningTrigger) => string | undefined;
    /** The pen's SFR band. */
    sfr: (pct: number | null) => KpiTone;
    /** The title of a cell whose source this pen has no mapping for. */
    unmapped: (state: FeedingSourceState) => string | undefined;
    /** The information class of a Bioscope cell. */
    bioscopeInfo: (tone: KpiTone) => string | undefined;
    /** The title naming a shaded cell's band. */
    info: (band: InfoBand) => string | undefined;
    /** `info` for a Bioscope cell. */
    bioscopeInfoTitle: (band: InfoBand) => string | undefined;
    /** Why a blank cell is blank. */
    reason: (metric: PenMetric, value: number | null, bioscope?: boolean) => string | undefined;
    /** The Feed vs plan cell's mark and title. */
    feedVsPlan: { className: string | undefined; title: string | undefined };
}

export function penRowCells(
    pen: FeedingPen,
    system: SystemLabels,
    t: TFunction,
    locale: Locale
): PenRowCells {
    // Judged against the PEN's own resolved targets, so a pen at a different growth stage
    // is not flagged for meeting its own plan.
    const warnings = penWarnings(pen);
    const collecting = pen.bioscopeDataQuality !== 'ready';
    const info = (band: InfoBand) => infoCellTitle(band, t, locale);
    const unmapped = (state: FeedingSourceState) => sourceTitle(state, system.cellNotConfigured);
    // A blank cell names its cause as the KPI row does: a failed feeding-service call, a
    // Bioscope still collecting or absent, else the unmapped-signal title.
    const reason = (metric: PenMetric, value: number | null, bioscope = false) =>
        cellReason(pen, metric, value != null && !(bioscope && collecting), system, t);
    // Marked and titled on the figure the cell prints, so a shortfall that rounds to "0%"
    // is not shaded as "Fed less than plan", as bFCR is judged at its shown precision (O7).
    const feedVsPlanShown = displayedPercent(pen.feedVsPlanPct, 0);
    return {
        warnings,
        collecting,
        breach: (trigger) => warningCellClass(penWarningTier(warnings, trigger)),
        // Bands come from the PEN's resolved config: a pen of smolt and one near harvest
        // have different healthy ranges, so a shared band would mark one of them wrongly.
        sfr: (pct) =>
            sfrTone(
                pct,
                pen.config.sfrHealthyMinPct,
                pen.config.sfrHealthyMaxPct,
                pen.config.sfrOverfeedPct
            ),
        unmapped,
        // Marks are suppressed while a value is masked, so a '–' cell never carries a
        // stale tone; penWarnings applies the same rule to the Bioscope triggers.
        bioscopeInfo: (tone) => (collecting ? undefined : infoCellClass(tone)),
        // A shaded cell names its band, so the colour is never the only account of it.
        info,
        bioscopeInfoTitle: (band) => (collecting ? undefined : info(band)),
        reason,
        feedVsPlan: {
            className: infoCellClass(feedVsPlanTone(feedVsPlanShown)),
            title:
                reason('feedVsPlan', pen.feedVsPlanPct) ??
                unmapped(pen.dataSources.feedPlan) ??
                joinTitles(
                    info({ kind: 'feedVsPlan', pct: feedVsPlanShown }),
                    planDaysTitle(pen, t)
                ),
        },
    };
}
